/**
 * Repository pro načítání dat pro Gantt Timeline
 * Načítá směny a odstávky (plánované i neplánované) pro zobrazení v gantt diagramu
 */

import { ShiftModel, PlanItemModel } from '@/libs/Api';
import { graphqlPost } from '@/libs/tpm-analytics/graphql/transport';
import { toIsoString } from '@/libs/tpm-analytics/graphql/queries';
import { GanttTimelineFilters } from '@/libs/tpm-analytics/core/gantt-timeline.types';

/**
 * GraphQL query pro načtení směn s pracovišti
 */
function buildShiftsQuery(where: string): string {
    return `query($from: DateTime!, $to: DateTime!) {
        shifts(pageSize: 999999, where: ${where}) {
            id
            fromDate
            toDate
            workplaceId
            workplace { 
                id 
                code 
                name 
                location { 
                    id 
                    name 
                } 
            }
            shiftTypeId
            shiftType { id name }
            shiftTeamTypeId
            shiftTeamType { id name }
        }
    }`;
}

/**
 * GraphQL query pro načtení odstávek (PlanItems)
 * DŮLEŽITÉ: Podporuje neukončené odstávky (shutdownToDate může být null)
 */
function buildDowntimesQuery(where: string): string {
    return `query($from: DateTime!, $to: DateTime!) {
        planItems(pageSize: 999999, where: ${where}) {
            id
            workplaceId
            workplace { 
                id 
                code 
                name 
                location { 
                    id 
                    name 
                } 
            }
            stationId
            station { id name }
            shutdownFromDate
            shutdownToDate
            shutdownDuration
            reactionDuration
            hourlyMaintenanceCosts
            hourlyDowntimeCosts
            hourlyDowntimeLostProfit
            auditFaultTypeId
            auditFaultType { 
                id 
                name 
                priority 
                color 
                isPlannedShutdown 
            }
            defectId
            defect { id code name }
            machineComponentId
            machineComponent { id code name }
            shiftId
            shiftTypeId
            name
            description
        }
    }`;
}

/**
 * Sestaví WHERE podmínku pro směny s podporou filtrů
 */
function buildShiftsWhere(filters?: GanttTimelineFilters): string {
    const listExpr = (ids?: string[]) => (ids && ids.length > 0 ? `[${ids.map((x) => `"${x}"`).join(', ')}]` : '[]');
    
    const parts: string[] = [
        `fromDate: { lt: $to }`, // Směna začíná před koncem rozsahu
        `toDate: { gt: $from }`, // Směna končí po začátku rozsahu (overlap detection)
    ];

    if (filters?.workplaceIds && filters.workplaceIds.length > 0) {
        parts.push(`workplaceId: { in: ${listExpr(filters.workplaceIds)} }`);
    }
    if (filters?.locationIds && filters.locationIds.length > 0) {
        parts.push(`workplace: { locationId: { in: ${listExpr(filters.locationIds)} } }`);
    }
    if (filters?.shiftTypeIds && filters.shiftTypeIds.length > 0) {
        parts.push(`shiftTypeId: { in: ${listExpr(filters.shiftTypeIds)} }`);
    }
    if (filters?.shiftTeamTypeIds && filters.shiftTeamTypeIds.length > 0) {
        parts.push(`shiftTeamTypeId: { in: ${listExpr(filters.shiftTeamTypeIds)} }`);
    }
    if (filters?.projectIds && filters.projectIds.length > 0) {
        parts.push(`shiftProductions: { some: { projectId: { in: ${listExpr(filters.projectIds)} } } }`);
    }

    return `{ ${parts.join(', ')} }`;
}

/**
 * Sestaví WHERE podmínku pro odstávky s podporou neukončených odstávek
 * 
 * KLÍČOVÁ LOGIKA:
 * - shutdownFromDate < $to (odstávka začala před koncem rozsahu)
 * - (shutdownToDate > $from OR shutdownToDate = null) (odstávka končí po začátku rozsahu NEBO stále trvá)
 * 
 * Toto zachytí i neukončené odstávky, které začaly před zobrazeným rozsahem.
 */
function buildDowntimesWhere(isPlanned: boolean, filters?: GanttTimelineFilters): string {
    const listExpr = (ids?: string[]) => (ids && ids.length > 0 ? `[${ids.map((x) => `"${x}"`).join(', ')}]` : '[]');
    
    const parts: string[] = [
        `isWorkplaceShutdown: { eq: true }`,
        `auditFaultType: { isPlannedShutdown: { eq: ${isPlanned ? 'true' : 'false'} } }`,
        `shutdownFromDate: { lt: $to }`, // Odstávka začala před koncem rozsahu
        // Odstávka končí po začátku rozsahu NEBO stále trvá (null)
        `or: [
            { shutdownToDate: { gt: $from } },
            { shutdownToDate: { eq: null } }
        ]`,
    ];

    if (filters?.workplaceIds && filters.workplaceIds.length > 0) {
        parts.push(`workplaceId: { in: ${listExpr(filters.workplaceIds)} }`);
    }
    if (filters?.locationIds && filters.locationIds.length > 0) {
        parts.push(`workplace: { locationId: { in: ${listExpr(filters.locationIds)} } }`);
    }
    if (filters?.shiftTypeIds && filters.shiftTypeIds.length > 0) {
        parts.push(`shift: { shiftTypeId: { in: ${listExpr(filters.shiftTypeIds)} } }`);
    }
    if (filters?.shiftTeamTypeIds && filters.shiftTeamTypeIds.length > 0) {
        parts.push(`shift: { shiftTeamTypeId: { in: ${listExpr(filters.shiftTeamTypeIds)} } }`);
    }
    if (filters?.projectIds && filters.projectIds.length > 0) {
        parts.push(`shift: { shiftProductions: { some: { projectId: { in: ${listExpr(filters.projectIds)} } } } }`);
    }

    return `{ ${parts.join(', ')} }`;
}

/**
 * Repository pro Gantt Timeline data
 */
export class GanttTimelineRepository {
    /**
     * Načte všechny směny pro daný časový rozsah a filtry
     */
    static async fetchShifts(
        fromIso: string,
        toIso: string,
        filters?: GanttTimelineFilters
    ): Promise<ShiftModel[]> {
        const where = buildShiftsWhere(filters);
        const query = buildShiftsQuery(where);
        
        if ((window as any).__TPM_GQL_DEBUG) {
            // eslint-disable-next-line no-console
            console.log('[TPM][Repo][GanttTimeline] fetchShifts', { from: fromIso, to: toIso, filters });
        }
        
        const data = await graphqlPost<any>(query, { 
            from: toIsoString(fromIso), 
            to: toIsoString(toIso) 
        });
        
        const list = data?.shifts ?? [];
        return Array.isArray(list) ? (list as ShiftModel[]) : [];
    }

    /**
     * Načte plánované odstávky (isPlannedShutdown = true)
     * Včetně neukončených odstávek (shutdownToDate = null)
     */
    static async fetchPlannedDowntimes(
        fromIso: string,
        toIso: string,
        filters?: GanttTimelineFilters
    ): Promise<PlanItemModel[]> {
        const where = buildDowntimesWhere(true, filters);
        const query = buildDowntimesQuery(where);
        
        if ((window as any).__TPM_GQL_DEBUG) {
            // eslint-disable-next-line no-console
            console.log('[TPM][Repo][GanttTimeline] fetchPlannedDowntimes', { from: fromIso, to: toIso, filters });
        }
        
        const data = await graphqlPost<any>(query, { 
            from: toIsoString(fromIso), 
            to: toIsoString(toIso) 
        });
        
        const list = data?.planItems ?? [];
        return Array.isArray(list) ? (list as PlanItemModel[]) : [];
    }

    /**
     * Načte neplánované odstávky (isPlannedShutdown = false)
     * Včetně neukončených odstávek (shutdownToDate = null)
     */
    static async fetchUnplannedDowntimes(
        fromIso: string,
        toIso: string,
        filters?: GanttTimelineFilters
    ): Promise<PlanItemModel[]> {
        const where = buildDowntimesWhere(false, filters);
        const query = buildDowntimesQuery(where);
        
        if ((window as any).__TPM_GQL_DEBUG) {
            // eslint-disable-next-line no-console
            console.log('[TPM][Repo][GanttTimeline] fetchUnplannedDowntimes', { from: fromIso, to: toIso, filters });
        }
        
        const data = await graphqlPost<any>(query, { 
            from: toIsoString(fromIso), 
            to: toIsoString(toIso) 
        });
        
        const list = data?.planItems ?? [];
        return Array.isArray(list) ? (list as PlanItemModel[]) : [];
    }

    /**
     * Načte všechna data najednou pro optimalizaci
     * Vrátí objekt se směnami, plánovanými a neplánovanými odstávkami
     */
    static async fetchAllData(
        fromIso: string,
        toIso: string,
        filters?: GanttTimelineFilters
    ): Promise<{
        shifts: ShiftModel[];
        plannedDowntimes: PlanItemModel[];
        unplannedDowntimes: PlanItemModel[];
    }> {
        const [shifts, plannedDowntimes, unplannedDowntimes] = await Promise.all([
            this.fetchShifts(fromIso, toIso, filters),
            this.fetchPlannedDowntimes(fromIso, toIso, filters),
            this.fetchUnplannedDowntimes(fromIso, toIso, filters),
        ]);

        return {
            shifts,
            plannedDowntimes,
            unplannedDowntimes,
        };
    }
}

